import { TimeRange } from '@mi-front/core';
import debugFn from 'debug';
import Player from 'video.js/dist/types/player';
import { createStore, StoreApi } from 'zustand/vanilla';
import {
  BasePlaybackActions,
  BasePlaybackState,
  createBasePlaybackSlice,
  ExclusivePlaybackMode,
} from '../store/base-playback-slice';
import { VideoJsMediaPlayer } from './video-js-media-player';

const debug = debugFn('video-playback');

export interface VideoPlaybackState extends BasePlaybackState {
  readonly videoJsPlayer: Player | null;
}

export interface VideoPlaybackActions extends BasePlaybackActions {
  setVideoJsPlayer: (player: Player | null) => void;
}

export type VideoPlaybackStore = VideoPlaybackState & VideoPlaybackActions;

/**
 * Creates a video playback store instance.
 * Optionally accepts initial source URL and playback range for bounded playback.
 */
export function createVideoPlaybackStore({
  src,
  playbackRange,
  exclusivePlaybackMode = 'pause',
  playbackId,
  autoPlay = false,
}: {
  src?: string | null;
  playbackRange?: TimeRange<number> | null;
  exclusivePlaybackMode?: ExclusivePlaybackMode;
  playbackId?: string;
  /** When true, start playback as soon as the Video.js player is registered. */
  autoPlay?: boolean;
}): StoreApi<VideoPlaybackStore> {
  const store: StoreApi<VideoPlaybackStore> = createStore<VideoPlaybackStore>()((set, get, api) => {
    return {
      ...createBasePlaybackSlice<VideoPlaybackStore>()(set, get, api),
      mediaSrc: src ?? null,
      playbackRange: playbackRange ?? null,
      exclusivePlaybackMode,
      playbackId,
      videoJsPlayer: null,
      setVideoJsPlayer: (videoJsPlayer) => {
        set({ videoJsPlayer });

        if (!videoJsPlayer) {
          return;
        }

        const baseTs = get().playbackRange?.from ?? 0;
        const id = get().playbackId;

        get().setupPlayer(
          new VideoJsMediaPlayer(videoJsPlayer!, {
            baseTs,
            playerId: id,
          }),
        );

        if (autoPlay) {
          void get().play();
        }
      },
    };
  });

  return store;
}

export type VideoPlaybackStoreApi = StoreApi<VideoPlaybackStore>;
